import { CircularProgress, DialogContent, DialogTitle } from '@material-ui/core'
import { CheckCircle } from '@material-ui/icons'
import { ClosableDialog } from 'components/controls/dialogs'
import { Button } from 'components/facets'
import { downloadV2MigrationCheckZip, getV2MigrationCheck, startV2MigrationCheck } from 'lib/api'
import { useSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import Accordion from 'react-bootstrap/Accordion'
import { AutoSizer, Column, Table } from 'react-virtualized'
import { CardLanguage, V2MigrationCheckRow, V2MigrationCheckState, V2MigrationReport } from 'shared'

const POLL_MS = 5000

const errorMessage = (e: unknown): string => {
  const error = (e as any)?.response?.data?.error
  return error?.message ?? error ?? (e as Error)?.message
}

type GameRow = V2MigrationCheckRow & { game: string }

interface Report {
  multiplePrices: { game: string; prices: number[]; articles: GameRow[] }[]
  usEdition: GameRow[]
  graded: GameRow[]
  possiblyGraded: GameRow[]
  taxation: GameRow[]
  finishType: GameRow[]
}

const emptyReport = (): Report => ({
  multiplePrices: [],
  usEdition: [],
  graded: [],
  possiblyGraded: [],
  taxation: [],
  finishType: [],
})

const addGame = (report: Report, game: string, gameReport: V2MigrationReport): Report => {
  const tag = (rows: V2MigrationCheckRow[]) => rows.map((row) => ({ ...row, game }))
  return {
    multiplePrices: [
      ...report.multiplePrices,
      ...gameReport.multiplePrices.map((entry) => ({
        game,
        prices: entry.prices,
        articles: tag(entry.articles),
      })),
    ],
    usEdition: [...report.usEdition, ...tag(gameReport.usEdition)],
    graded: [...report.graded, ...tag(gameReport.graded)],
    // Reports saved before this list existed do not have it.
    possiblyGraded: [...report.possiblyGraded, ...tag(gameReport.possiblyGraded ?? [])],
    taxation: [...report.taxation, ...tag(gameReport.taxation)],
    finishType: [...report.finishType, ...tag(gameReport.finishType)],
  }
}

const HEADER_HEIGHT = 40
const ROW_HEIGHT = 50
const PRICE_LINE_HEIGHT = 20
// Past this the table scrolls inside its section instead of making the whole dialog scroll.
const MAX_TABLE_HEIGHT = 400
const MIN_TABLE_WIDTH = 900

// Comments are free text of any length: wrapped in the cell and whole on hover.
const wrappedCell = (text: string | undefined) => (
  <div className="v2-check-wrapped-cell" title={text}>
    {text}
  </div>
)

const ArticlesTable = ({ rows, extra }: { rows: GameRow[]; extra?: 'finishType' }) => (
  <AutoSizer disableHeight>
    {({ width }) => (
      <Table
        width={Math.max(width, MIN_TABLE_WIDTH)}
        height={Math.min(rows.length * ROW_HEIGHT + HEADER_HEIGHT, MAX_TABLE_HEIGHT)}
        headerHeight={HEADER_HEIGHT}
        rowHeight={ROW_HEIGHT}
        rowCount={rows.length}
        rowGetter={({ index }) => rows[index]}
      >
        <Column width={90} label="Game" dataKey="game" />
        <Column
          width={200}
          flexGrow={2}
          label="Card"
          dataKey="name"
          cellRenderer={({ rowData }) => wrappedCell(rowData.name)}
        />
        <Column
          width={150}
          flexGrow={1}
          label="Expansion"
          dataKey="expansion"
          cellRenderer={({ rowData }) => wrappedCell(rowData.expansion)}
        />
        <Column width={60} label="Cond." dataKey="condition" />
        <Column
          width={90}
          label="Lang."
          dataKey="idLanguage"
          cellRenderer={({ rowData }) => CardLanguage[rowData.idLanguage]}
        />
        <Column width={50} label="Qty" dataKey="quantity" />
        <Column width={70} label="Price" dataKey="price" />
        <Column
          width={200}
          flexGrow={2}
          label="Comment"
          dataKey="comment"
          cellRenderer={({ rowData }) => wrappedCell(rowData.comment)}
        />
        {extra === 'finishType' && (
          <Column
            width={160}
            label="Recognized as"
            dataKey="recognizedFinishType"
            cellRenderer={({ rowData }) =>
              rowData.recognizedFinishType && (
                <span className="d-flex align-items-center gap-1">
                  <CheckCircle fontSize="small" color="primary" />
                  {rowData.recognizedFinishType}
                </span>
              )
            }
          />
        )}
      </Table>
    )}
  </AutoSizer>
)

// One row per listing sold at several prices, tall enough to show every price on its own line.
const MultiplePricesTable = ({ entries }: { entries: Report['multiplePrices'] }) => {
  const rowHeight = (index: number) =>
    Math.max(ROW_HEIGHT, entries[index].prices.length * PRICE_LINE_HEIGHT + 10)
  const totalHeight = entries.reduce((total, _entry, index) => total + rowHeight(index), 0)

  return (
    <AutoSizer disableHeight>
      {({ width }) => (
        <Table
          width={Math.max(width, MIN_TABLE_WIDTH)}
          height={Math.min(totalHeight + HEADER_HEIGHT, MAX_TABLE_HEIGHT)}
          headerHeight={HEADER_HEIGHT}
          rowHeight={({ index }) => rowHeight(index)}
          rowCount={entries.length}
          rowGetter={({ index }) => entries[index]}
        >
          <Column width={90} label="Game" dataKey="game" />
          <Column
            width={200}
            flexGrow={2}
            label="Card"
            dataKey="name"
            cellRenderer={({ rowData }) => wrappedCell(rowData.articles[0].name)}
          />
          <Column
            width={150}
            flexGrow={1}
            label="Expansion"
            dataKey="expansion"
            cellRenderer={({ rowData }) => wrappedCell(rowData.articles[0].expansion)}
          />
          <Column
            width={60}
            label="Cond."
            dataKey="condition"
            cellRenderer={({ rowData }) => rowData.articles[0].condition}
          />
          <Column
            width={90}
            label="Lang."
            dataKey="idLanguage"
            cellRenderer={({ rowData }) => CardLanguage[rowData.articles[0].idLanguage]}
          />
          <Column
            width={50}
            label="Qty"
            dataKey="quantity"
            cellRenderer={({ rowData }) =>
              rowData.articles.reduce(
                (total: number, article: GameRow) => total + article.quantity,
                0
              )
            }
          />
          <Column
            width={90}
            label="Prices"
            dataKey="prices"
            cellRenderer={({ rowData }) =>
              rowData.prices.map((price: number) => <div key={price}>{price}</div>)
            }
          />
        </Table>
      )}
    </AutoSizer>
  )
}

const Section = ({
  title,
  count,
  children,
}: {
  title: string
  count: number
  children: JSX.Element
}) =>
  count === 0 ? null : (
    <Accordion.Item eventKey={title}>
      <Accordion.Header>
        {title} ({count})
      </Accordion.Header>
      <Accordion.Body>{children}</Accordion.Body>
    </Accordion.Item>
  )

// The report of one or more games, shown the same way whether the stock came from Cardmarket or from
// an uploaded CSV. `incomplete` holds back the "nothing to report" line while some game is not (yet)
// checked.
export const V2MigrationReportView = ({
  reports,
  incomplete,
}: {
  reports: { game: string; report: V2MigrationReport }[]
  incomplete?: boolean
}): JSX.Element => {
  const report = reports.reduce(
    (merged, { game, report: gameReport }) => addGame(merged, game, gameReport),
    emptyReport()
  )

  if (!incomplete && Object.values(report).every((rows: unknown[]) => rows.length === 0)) {
    return <p>Nothing in the stock blocks the move to v2.</p>
  }

  return (
    <Accordion alwaysOpen>
      <Section
        title="Some articles have multiple prices for the same listing"
        count={report.multiplePrices.length}
      >
        <MultiplePricesTable entries={report.multiplePrices} />
      </Section>
      <Section title="Some articles will be marked as US Edition" count={report.usEdition.length}>
        <ArticlesTable rows={report.usEdition} />
      </Section>
      <Section title="Some articles are graded cards" count={report.graded.length}>
        <ArticlesTable rows={report.graded} />
      </Section>
      <Section
        title="Some articles may be graded by a company we do not recognize"
        count={report.possiblyGraded.length}
      >
        <ArticlesTable rows={report.possiblyGraded} />
      </Section>
      <Section
        title="Some articles have comments that identify the taxation"
        count={report.taxation.length}
      >
        <ArticlesTable rows={report.taxation} />
      </Section>
      <Section
        title="Some articles mention a finish type in the comment"
        count={report.finishType.length}
      >
        <ArticlesTable rows={report.finishType} extra="finishType" />
      </Section>
    </Accordion>
  )
}

// The run itself goes on on the server (exports every game, checks it, zips the CSVs) and is kept
// there for three hours: the dialog only starts it when there is none and reads where it got to, so it
// can be closed and reopened, even from another tab, without starting over.
export const V2MigrationCheckDialog = ({
  open,
  onClose,
  userId,
}: {
  open: boolean
  onClose: () => void
  userId: string
}): JSX.Element => {
  const { enqueueSnackbar } = useSnackbar()
  const [state, setState] = useState<V2MigrationCheckState | null>(null)
  const [running, setRunning] = useState(false)
  // Bumped by "Run again" to follow the run it just started.
  const [runCount, setRunCount] = useState(0)

  useEffect(() => {
    if (!open) return

    let active = true
    let timer: ReturnType<typeof setTimeout>

    const follow = async (startIfNone: boolean) => {
      try {
        let current = await getV2MigrationCheck(userId)
        if (startIfNone && !current.running && !current.state) {
          await startV2MigrationCheck(userId)
          current = await getV2MigrationCheck(userId)
        }
        if (!active) return

        setState(current.state)
        setRunning(current.running)
        if (current.running) {
          timer = setTimeout(() => follow(false), POLL_MS)
        }
      } catch (e) {
        enqueueSnackbar(`Could not check the migration to v2: ${errorMessage(e)}`, {
          variant: 'error',
        })
      }
    }

    follow(true)

    return () => {
      active = false
      clearTimeout(timer)
    }
  }, [open, userId, runCount])

  const handleRunAgain = async () => {
    try {
      await startV2MigrationCheck(userId)
      setRunCount((count) => count + 1)
    } catch (e) {
      enqueueSnackbar(`Could not start the check: ${errorMessage(e)}`, { variant: 'error' })
    }
  }

  const handleDownloadZip = async () => {
    try {
      const res = await downloadV2MigrationCheckZip(userId)
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }))
      const a = document.createElement('a')
      a.href = url
      a.download = `export-${userId}-all-games.zip`
      a.click()
      window.URL.revokeObjectURL(url)
    } catch (e) {
      enqueueSnackbar(`Could not download the zip: ${errorMessage(e)}`, { variant: 'error' })
    }
  }

  const failedGames = state?.failedGames ?? []
  const interrupted = state?.status === 'running' && !running

  return (
    <ClosableDialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <div className="m-4">
        <DialogTitle disableTypography>
          <h3>Check migration to v2</h3>
        </DialogTitle>
        <DialogContent>
          {running && (
            <div className="d-flex align-items-center mb-3">
              <CircularProgress size={20} className="mr-2" />
              <p className="m-0">
                Exporting and checking {state?.currentGame} ({(state?.checkedGames ?? 0) + 1}/
                {state?.totalGames}). This takes several minutes: you can close this dialog and come
                back later.
              </p>
            </div>
          )}
          {!running && state && (
            <div className="d-flex align-items-center justify-content-between mb-3">
              {interrupted ? (
                <p className="m-0 text-danger">The last check was interrupted before the end.</p>
              ) : (
                <p className="m-0">Checked on {new Date(state.updatedAt).toLocaleString()}.</p>
              )}
              <div className="d-flex gap-2">
                <Button variant="outlined" onClick={handleRunAgain}>
                  Run again
                </Button>
                {state.status === 'done' && !state.zipError && (
                  <Button variant="contained" onClick={handleDownloadZip}>
                    Download zip
                  </Button>
                )}
              </div>
            </div>
          )}
          {state?.zipError && <p className="text-danger">Zip not saved: {state.zipError}</p>}
          {failedGames.map(({ game, error }) => (
            <p key={game} className="text-danger">
              {game} not checked: {error}
            </p>
          ))}

          {state && (
            <V2MigrationReportView
              reports={state.reports}
              incomplete={state.status !== 'done' || failedGames.length > 0}
            />
          )}
        </DialogContent>
      </div>
    </ClosableDialog>
  )
}
